import { canGenerateMissingPocoCategory, getActiveRoundsCategoryKey, groupCategoryRounds } from "../utils/categoryRounds";
import PocoGeneratedCourtsEditor from "./PocoGeneratedCourtsEditor";
import type { AdminRoundMatch } from "../../challenges/utils/pocoSchedule";
import AdminCategoryRoundsPanel from "./AdminCategoryRoundsPanel";
import type {
  ComponentType,
  Dispatch,
  ReactNode,
  SetStateAction,
  SVGProps,
} from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OrganizerDatePicker, {
  formatDateInputValue,
  parseDateInputValue,
} from "../../../shared/components/OrganizerDatePicker";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { extractDatePart } from "../../../shared/utils/dateDrafts";
import {
  FiAward,
  FiCalendar,
  FiChevronDown,
  FiEdit3,
  FiLock,
  FiMapPin,
  FiPlay,
  FiRefreshCw,
  FiTag,
  FiUnlock,
  FiX,
} from "react-icons/fi";
import Button from "../../../shared/components/Button";
import ConfirmModal from "../../../shared/components/ConfirmModal";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  getOrganizerVisualIdentity,
  saveOrganizerVisualIdentity,
} from "../../auth/shared/services/authService";
import type { AuthUser } from "../../auth/shared/types/auth";
import type {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeDetail,
  EventIdentityMode,
} from "../../challenges/types/challengeAdmin";
import type {
  ChallengeEventDraft,
  DraftPair,
  DraftPlayer,
  DraftSideChoice,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  updateChallengeMatchStatus,
  updateTournamentParticipantCheckin,
  type AdminAvailabilityRestrictionPayload,
  type AdminEventRegistration,
  type ParticipantCheckin,
} from "../services/eventRegistrationAdminService";
import { mergeChallengeRoundsPreservingLocalScores } from "../utils/challengeRoundsMerge";
import type { TournamentRegistrationCategoryItem } from "../utils/tournamentCategories";
import { resolveAdminParticipantNotice } from "../utils/adminParticipantNotice";
import type { MatchCheckInSide } from "./AdminMatchCheckInControl";
import type { TournamentCategoryEditorField } from "./TournamentCategoriesEditor";
import AdminChallengeGeneralForm from "./AdminChallengeGeneralForm";
import {
  AdminChallengeEventAmountField,
  AdminChallengeEventImageField,
  AdminChallengeShirtSettingsFields,
} from "./AdminChallengeEventSettingsFields";
import AdminChallengeScheduleFields from "./AdminChallengeScheduleFields";
import AdminChallengeParticipantControls from "./AdminChallengeParticipantControls";
import AdminChallengeRoundsEditor from "./AdminChallengeRoundsEditor";
import AdminDrawPairsSummary from "./AdminDrawPairsSummary";
import AdminEventIdentityFields from "./AdminEventIdentityFields";
import AdminSelectedPairsList from "./AdminSelectedPairsList";
import AdminSelectedPlayersList from "./AdminSelectedPlayersList";
import AdminSuper8Groups from "./AdminSuper8Groups";
import AdminTournamentCategoryTabs from "./AdminTournamentCategoryTabs";
import AdminVenueFields from "./AdminVenueFields";
import AdminTournamentOperationsPanel, {
  type AdminTournamentOperationsPanelHandle,
} from "./AdminTournamentOperationsPanel";
import AdminTournamentEventSchedulePanel from "./AdminTournamentEventSchedulePanel";
import AdminAvailabilityRestrictionModal from "./AdminAvailabilityRestrictionModal";
import TournamentCategoryModal from "./TournamentCategoryModal";
import TournamentAdvancedSettingsFields from "../../tournamentAvailability/components/TournamentAdvancedSettingsFields";
import type { TournamentCategoryModalState } from "../../tournaments/types/tournamentAdmin";

type PaymentMethod = "pix" | "credit_card" | "debit_card";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const AwardIcon = FiAward as unknown as IconLike;
const CalendarIcon = FiCalendar as unknown as IconLike;
const ChevronDownIcon = FiChevronDown as unknown as IconLike;
const EditIcon = FiEdit3 as unknown as IconLike;
const LockIcon = FiLock as unknown as IconLike;
const MapPinIcon = FiMapPin as unknown as IconLike;
const PlayIcon = FiPlay as unknown as IconLike;
const RefreshIcon = FiRefreshCw as unknown as IconLike;
const TagIcon = FiTag as unknown as IconLike;
const UnlockIcon = FiUnlock as unknown as IconLike;
const XIcon = FiX as unknown as IconLike;

type TournamentParticipantsSummary = Record<
  string,
  {
    current: number;
    limit: number;
    registrationType: "individual" | "fixed_pair";
  }
>;

type PairCapacity = {
  exceeds: boolean;
  limit: number;
};

type EditorSnapshot = {
  general: unknown;
  participants: unknown;
  rounds: unknown;
  shirts: unknown;
};

type SaveChallengeOptions = {
  includeRounds?: boolean;
  roundsOverride?: any[][];
  roundsOnly?: boolean;
};

type TournamentEventSchedulePanelHandle = {
  savePendingChanges: () => Promise<boolean>;
};

function sameSnapshotBlock(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isRequiredTextMissing(value: unknown) {
  const normalized = String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return (
    !normalized ||
    normalized === "a definir" ||
    normalized === "nao informado"
  );
}

function normalizeChallengeMatchStatus(match: any) {
  const rawStatus = String(
    match?.status || match?.matchStatus || match?.courtStatus || "",
  )
    .trim()
    .toLowerCase();
  if (rawStatus === "in_progress" || rawStatus === "em_quadra") {
    return "in_progress";
  }
  if (rawStatus === "finished" || rawStatus === "encerrado") {
    return "finished";
  }
  return rawStatus || "pending";
}

function normalizeEditorRoundsSnapshot(rounds: any[]) {
  return (Array.isArray(rounds) ? rounds : []).map((round: any[]) =>
    (Array.isArray(round) ? round : []).map((match: any) => ({
      id: match?.id === null || match?.id === undefined ? "" : String(match.id),
      categoryKey: String(match?.categoryKey || ""),
      dupla1Id: String(match?.dupla1Id || "").trim(),
      dupla2Id: String(match?.dupla2Id || "").trim(),
      score1:
        match?.score1 === "" || match?.score1 === null || match?.score1 === undefined
          ? ""
          : Number(match.score1),
      score2:
        match?.score2 === "" || match?.score2 === null || match?.score2 === undefined
          ? ""
          : Number(match.score2),
      status: normalizeChallengeMatchStatus(match),
    })),
  );
}

type EditorAccordionSectionProps = {
  children: ReactNode;
  closeLabel?: string;
  footerActions?: ReactNode;
  icon: ReactNode;
  id: string;
  isOpen: boolean;
  keepMountedWhenClosed?: boolean;
  mode?: "accordion" | "modal";
  readOnly?: boolean;
  onSave?: () => boolean | void | Promise<boolean | void>;
  onToggle: (id: string) => void;
  canSave?: boolean;
  saveLabel?: string;
  saving?: boolean;
  savingLabel?: string;
  status: "complete" | "pending" | "attention" | "in_progress";
  subtitle: string;
  title: string;
};

function EditorAccordionSection({
  children,
  closeLabel = siteTexts.common.close,
  footerActions,
  icon,
  id,
  isOpen,
  keepMountedWhenClosed = false,
  mode = "accordion",
  readOnly = false,
  onSave,
  onToggle,
  canSave = true,
  saveLabel = siteTexts.admin.createChallengePage.summary.saveChanges,
  saving = false,
  savingLabel = siteTexts.admin.historyList.saving,
  status,
  subtitle,
  title,
}: EditorAccordionSectionProps) {
  const isModalMode = mode === "modal";
  useBodyScrollLock(isModalMode && isOpen);
  const contentId = `admin-edit-section-${id}`;
  const modalTitleId = `admin-edit-section-title-${id}`;
  const closeModal = useCallback(() => onToggle(id), [id, onToggle]);
  const statusLabel =
    status === "complete"
      ? siteTexts.admin.createChallengePage.sectionStatus.complete
      : status === "attention"
        ? siteTexts.admin.createChallengePage.sectionStatus.attention
        : status === "in_progress"
          ? siteTexts.challengeEngine.courtStatus.onCourt
          : siteTexts.admin.createChallengePage.sectionStatus.pending;
  const handleSave = async () => {
    if (!onSave || readOnly || saving || !canSave) return;
    const result = await onSave();
    if (result !== false) onToggle(id);
  };
  useEffect(() => {
    if (!isModalMode || !isOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeModal, isModalMode, isOpen]);
  const sectionContent = readOnly ? (
    <fieldset className="admin-editor-readonly-fields" disabled>
      {children}
    </fieldset>
  ) : (
    children
  );
  return (
    <section
      className={`admin-event-form-card admin-event-form-accordion is-${status}${
        isOpen ? " is-open" : ""
      } admin-event-form-section-${id}${
        isModalMode ? " admin-event-form-modal-trigger-card" : ""
      }`}
      data-readonly={readOnly || undefined}
    >
      <button
        type="button"
        className="admin-event-form-card-header"
        aria-expanded={isModalMode ? undefined : isOpen}
        aria-haspopup={isModalMode ? "dialog" : undefined}
        aria-controls={isModalMode && isOpen ? contentId : contentId}
        onClick={() => onToggle(id)}
      >
        <span className="admin-event-form-card-icon" aria-hidden="true">{icon}</span>
        <span className="admin-event-form-card-heading">
          <strong>{title}</strong>
          <small>{subtitle}</small>
        </span>
        <span className={`admin-event-form-status admin-event-form-status--${status}`}>
          {statusLabel}
        </span>
        <span className="admin-event-form-chevron" aria-hidden="true">
          {isModalMode ? <EditIcon /> : <ChevronDownIcon />}
        </span>
      </button>
      {isModalMode ? (
        <>
          {isOpen ? (
            <div
              className="admin-modal-backdrop admin-section-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeModal();
              }}
            >
              <div
                id={contentId}
                className="admin-modal admin-tournament-modal admin-section-edit-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={modalTitleId}
              >
                <div className="admin-modal-header">
                  <div>
                    <h3 id={modalTitleId}>{title}</h3>
                    <p>{subtitle}</p>
                  </div>
                  <button
                    type="button"
                    className="admin-modal-icon-close"
                    onClick={closeModal}
                    aria-label={closeLabel}
                    title={closeLabel}
                  >
                    <XIcon aria-hidden="true" />
                  </button>
                </div>
                <div className="admin-section-edit-modal-content">
                  {sectionContent}
                </div>
                <div className="admin-modal-actions admin-section-edit-modal-actions">
                  {!readOnly && footerActions}
                  {onSave && !readOnly && (
                    <Button
                      type="button"
                      variant="primary"
                      disabled={saving || !canSave}
                      onClick={() => void handleSave()}
                    >
                      {saving ? savingLabel : saveLabel}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : keepMountedWhenClosed ? (
            <div className="admin-section-keepalive" hidden aria-hidden="true">
              {sectionContent}
            </div>
          ) : null}
        </>
      ) : (
        <div id={contentId} className="admin-event-form-card-content" hidden={!isOpen}>
          {sectionContent}
        </div>
      )}
    </section>
  );
}

type AdminChallengeEditorPanelProps = {
  activeTournamentCategory: TournamentRegistrationCategoryItem | null;
  activeTournamentCategoryKey: string;
  activeTournamentCategoryPairCapacity: PairCapacity;
  adminPaymentMethod: PaymentMethod;
  availableUsersForChallenge: AuthUser[];
  canAddRegistrationsForChallenge: boolean;
  canFinishChallenge: boolean;
  canGenerateFixedPairRounds: boolean;
  canGeneratePlayerBasedRounds: boolean;
  canStartChallenge: boolean;
  categoryGenderLabel: (gender?: string | null) => string;
  categoryLabel: (category?: string | null) => string;
  categoryOptions: ChallengeCategoryCode[];
  categorySaveRevision?: number;
  challengeDetail: ChallengeDetail;
  challengeDurationDraft: string;
  challengeEventDraft: ChallengeEventDraft;
  challengeEventRegistrationsLoading: boolean;
  challengeRegistrationByCpf: Record<string, AdminEventRegistration>;
  challengeRegistrationByPairId: Record<string, AdminEventRegistration>;
  challengeScheduledAtDraft: Date | null;
  challengeScheduledEndAtDraft: Date | null;
  challengeSelectedParticipantsCount: number;
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  currentUserId?: number | string | null;
  currentPairMode: PairMode;
  draftPairs: DraftPair[];
  draftPlayers: DraftPlayer[];
  drawCpf: string;
  genderOptions: ChallengeCategoryGender[];
  hasRegistrationCategories: boolean;
  isAdmin: boolean;
  isFixedPairOnlyChallenge: boolean;
  isPoco: boolean;
  isSuper8: boolean;
  isTournamentChallenge: boolean;
  manualRegistrationEventIdForChallenge: string;
  maxDrawPlayersForCurrentChallenge: number | null;
  maxFixedPairsForCurrentChallenge: number | null;
  nameByCpf: Record<string, string>;
  onAddDrawPlayer: (rawValue?: string) => boolean;
  onAddFixedPair: () => void;
  onAddTournamentCategory: () => void;
  onClearRoundWinner: (roundIndex: number, matchIndex: number) => void;
  onCopyPaymentLink: (
    id: number,
    paymentMethod?: PaymentMethod,
    existingPaymentLink?: string | null,
  ) => void;
  onDeleteChallenge: () => void;
  onDeleteRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onEditTournamentCategory: (categoryKey: string) => void;
  onEditTournamentPair: (registration?: AdminEventRegistration | null) => void;
  onError: (message: string) => void;
  onExportShirtSizes: () => void;
  onFinishChallenge: () => void;
  onGenerateFixedPairRounds: () => boolean | void | Promise<boolean | void>;
  onGeneratePlayerBasedRounds: () => boolean | void | Promise<boolean | void>;
  onStartChallenge: () => void | Promise<void>;
  onToggleRegistrations: () => void | Promise<void>;
  onLoadChallengeEventRegistrations: (eventId?: string | null) => void;
  onMarkPaidRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRejectRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRemovePair: (pairId: string) => void;
  onRemovePlayer: (cpf: string) => void;
  onRemoveSuper8Player: (cpf: string) => void;
  onRemoveTournamentCategory: (categoryKey: string) => void;
  onCloseTournamentCategoryModal: () => void;
  onSaveChallenge: (
    options?: SaveChallengeOptions,
  ) => boolean | void | Promise<boolean | void>;
  onSaveAvailabilityRestriction: (
    id: number,
    payload: AdminAvailabilityRestrictionPayload,
  ) => Promise<{
    impactedCount?: number;
    lockedCount?: number;
  } | void>;
  onSaveTournamentCategory: () => void | Promise<void>;
  onSelectTournamentCategory: (categoryKey: string) => void;
  onSuccess: (message: string) => void;
  onUpdatePlayerSide: (
    cpf: string,
    sideChoice: DraftSideChoice,
  ) => void | Promise<void>;
  onUpdatePocoWinner: (
    roundIndex: number,
    matchIndex: number,
    winnerSide: "dupla1" | "dupla2" | "",
  ) => void;
  onUpdateRoundScore: (
    roundIndex: number,
    matchIndex: number,
    field: "score1" | "score2",
    value: string,
  ) => void;
  organizerUsers: AuthUser[];
  pairCpf1: string;
  pairCpf2: string;
  pairLabelById: Record<string, string>;
  pairMode: PairMode;
  resolvePlayerCpfInput: (value: string) => string;
  resolveRoundPairLabelWithPoints: (
    pairId: string,
    roundIndex: number,
  ) => string;
  resolveSideLabel: (side: DraftPlayer["side"]) => string;
  rounds: any[];
  registrationsToggleLoading: boolean;
  savingChallenge: boolean;
  savingDrawPairs: boolean;
  scoreMax: number;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeDurationDraft: Dispatch<SetStateAction<string>>;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
  setChallengeRoundsJson: Dispatch<SetStateAction<string>>;
  setChallengeScheduledAtDraft: Dispatch<SetStateAction<Date | null>>;
  setChallengeScheduledEndAtDraft: Dispatch<SetStateAction<Date | null>>;
  setDrawCpf: Dispatch<SetStateAction<string>>;
  setPairCpf1: Dispatch<SetStateAction<string>>;
  setPairCpf2: Dispatch<SetStateAction<string>>;
  setPairMode: Dispatch<SetStateAction<PairMode>>;
  showClearRoundWinnerAction: boolean;
  super8GroupAPlayers: DraftPlayer[];
  super8WaitingPlayers: DraftPlayer[];
  token: string;
  tournamentCategoryModal?: TournamentCategoryModalState | null;
  tournamentParticipantsSummaryByCategory: TournamentParticipantsSummary;
  updateTournamentCategoryModalDraft?: (
    field: TournamentCategoryEditorField,
    value: string,
  ) => void;
};

export default function AdminChallengeEditorPanel({
  activeTournamentCategory,
  activeTournamentCategoryKey,
  activeTournamentCategoryPairCapacity,
  adminPaymentMethod,
  availableUsersForChallenge,
  canAddRegistrationsForChallenge,
  canFinishChallenge,
  canGenerateFixedPairRounds,
  canGeneratePlayerBasedRounds,
  canStartChallenge,
  categoryGenderLabel,
  categoryLabel,
  categoryOptions,
  categorySaveRevision = 0,
  challengeDetail,
  challengeDurationDraft,
  challengeEventDraft,
  challengeEventRegistrationsLoading,
  challengeRegistrationByCpf,
  challengeRegistrationByPairId,
  challengeScheduledAtDraft,
  challengeScheduledEndAtDraft,
  challengeSelectedParticipantsCount,
  challengeTournamentCategories,
  currentUserId,
  currentPairMode,
  draftPairs,
  draftPlayers,
  drawCpf,
  genderOptions,
  hasRegistrationCategories,
  isAdmin,
  isFixedPairOnlyChallenge,
  isPoco,
  isSuper8,
  isTournamentChallenge,
  manualRegistrationEventIdForChallenge,
  maxDrawPlayersForCurrentChallenge,
  maxFixedPairsForCurrentChallenge,
  nameByCpf,
  onAddDrawPlayer,
  onAddFixedPair,
  onAddTournamentCategory,
  onClearRoundWinner,
  onCopyPaymentLink,
  onDeleteChallenge,
  onDeleteRegistration,
  onEditTournamentCategory,
  onEditTournamentPair,
  onError,
  onExportShirtSizes,
  onFinishChallenge,
  onGenerateFixedPairRounds,
  onGeneratePlayerBasedRounds,
  onStartChallenge,
  onToggleRegistrations,
  onLoadChallengeEventRegistrations,
  onMarkPaidRegistration,
  onRejectRegistration,
  onRemovePair,
  onRemovePlayer,
  onRemoveSuper8Player,
  onRemoveTournamentCategory,
  onCloseTournamentCategoryModal,
  onSaveChallenge,
  onSaveAvailabilityRestriction,
  onSaveTournamentCategory,
  onSelectTournamentCategory,
  onSuccess,
  onUpdatePlayerSide,
  onUpdatePocoWinner,
  onUpdateRoundScore,
  organizerUsers,
  pairCpf1,
  pairCpf2,
  pairLabelById,
  pairMode,
  resolvePlayerCpfInput,
  resolveRoundPairLabelWithPoints,
  resolveSideLabel,
  rounds,
  registrationsToggleLoading,
  savingChallenge,
  savingDrawPairs,
  scoreMax,
  setChallengeDetail,
  setChallengeDurationDraft,
  setChallengeEventDraft,
  setChallengeRoundsJson,
  setChallengeScheduledAtDraft,
  setChallengeScheduledEndAtDraft,
  setDrawCpf,
  setPairCpf1,
  setPairCpf2,
  setPairMode,
  showClearRoundWinnerAction,
  super8GroupAPlayers,
  super8WaitingPlayers,
  token,
  tournamentCategoryModal,
  tournamentParticipantsSummaryByCategory,
  updateTournamentCategoryModalDraft,
}: AdminChallengeEditorPanelProps) {
  const t = siteTexts.admin;
  const operationTexts = t.createChallengePage.operations;
  const tournamentOperationsRef =
    useRef<AdminTournamentOperationsPanelHandle>(null);
  const tournamentEventScheduleRef =
    useRef<TournamentEventSchedulePanelHandle>(null);
  const [pendingCheckInSideId, setPendingCheckInSideId] = useState<string | null>(null);
  const [pendingCourtMatchId, setPendingCourtMatchId] = useState<number | null>(null);
  const [organizerLogoSaving, setOrganizerLogoSaving] = useState(false);
  const [activeEditSection, setActiveEditSection] = useState("");
  const [editorBaselineSnapshot, setEditorBaselineSnapshot] =
    useState<EditorSnapshot | null>(null);
  const [baselineRefreshRequest, setBaselineRefreshRequest] = useState(0);
  const [tournamentPendingChangeItems, setTournamentPendingChangeItems] =
    useState<string[]>([]);
  const [
    tournamentEventSchedulePendingChangeItems,
    setTournamentEventSchedulePendingChangeItems,
  ] = useState<string[]>([]);
  const [restrictionModalRegistration, setRestrictionModalRegistration] =
    useState<AdminEventRegistration | null>(null);
  const [restrictionSaving, setRestrictionSaving] = useState(false);
  const [confirmCloseRegistrations, setConfirmCloseRegistrations] =
    useState(false);
  const [startingChallenge, setStartingChallenge] = useState(false);
  const lastBaselineChallengeIdRef = useRef<number | null>(null);
  const lastCategorySaveRevisionRef = useRef(categorySaveRevision);
  const isTournamentOperations =
    String(challengeDetail.challengeType || "").trim().toLowerCase() ===
    "tournament";
  const isCategorizedChallenge =
    hasRegistrationCategories && !isTournamentChallenge;
  const useCategoryRoundsAccordion =
    isCategorizedChallenge && challengeTournamentCategories.length > 1;
  const tournamentScheduleSettingsHostId = `admin-tournament-schedule-settings-${challengeDetail.id}`;
  const tournamentCourtSettingsHostId = `admin-tournament-court-settings-${challengeDetail.id}`;
  const canManageOrganizerLogo =
    Boolean(token) &&
    !challengeDetail.isOfficial &&
    Number(currentUserId || 0) > 0 &&
    Number(currentUserId || 0) === Number(challengeDetail.organizerUserId || 0);

  const normalizeCpf = (value: string | null | undefined) =>
    String(value || "").replace(/\D/g, "").slice(0, 11);

  const replaceCheckins = (list: ParticipantCheckin[]) => {
    setChallengeDetail((current) =>
      current ? { ...current, participantCheckins: list } : current,
    );
  };

  const buildOptimisticCheckins = (
    current: ParticipantCheckin[],
    cpfs: string[],
    checkedIn: boolean,
    matchId?: number | null,
  ) => {
    const now = new Date().toISOString();
    const map = new Map<string, ParticipantCheckin>();
    const normalizedMatchId = Number(matchId || 0) || null;
    const keyFor = (cpf: string, itemMatchId?: number | null) => {
      const currentMatchId = Number(itemMatchId || 0);
      return currentMatchId > 0 ? `${currentMatchId}:${cpf}` : cpf;
    };
    current.forEach((item) => {
      const cpf = normalizeCpf(item.cpf);
      if (cpf) map.set(keyFor(cpf, item.matchId), item);
    });
    cpfs.forEach((cpf) => {
      const key = keyFor(cpf, normalizedMatchId);
      if (checkedIn) {
        map.set(key, {
          cpf,
          matchId: normalizedMatchId,
          checkedIn: true,
          checkedInAt: now,
        });
      } else {
        map.delete(key);
      }
    });
    return Array.from(map.values());
  };

  const toggleCheckInSide = async (
    side: MatchCheckInSide,
    checkedIn: boolean,
  ) => {
    const cpfs = Array.from(
      new Set(side.participantCpfs.map(normalizeCpf).filter((cpf) => cpf.length >= 8)),
    );
    if (cpfs.length === 0 || pendingCheckInSideId) return;
    const previous = challengeDetail.participantCheckins || [];
    const matchId = Number(side.matchId || 0) || null;
    setPendingCheckInSideId(side.id);
    replaceCheckins(buildOptimisticCheckins(previous, cpfs, checkedIn, matchId));
    try {
      let next = previous;
      for (const cpf of cpfs) {
        const response = await updateTournamentParticipantCheckin(
          token,
          Number(challengeDetail.id),
          cpf,
          checkedIn,
          matchId,
        );
        next = response.participantCheckins || [];
      }
      replaceCheckins(next);
    } catch (error) {
      replaceCheckins(previous);
      onError(
        error instanceof Error
          ? error.message
          : checkedIn
            ? siteTexts.challengeEngine.checkIn.confirmError
            : siteTexts.challengeEngine.checkIn.undoError,
      );
    } finally {
      setPendingCheckInSideId(null);
    }
  };

  const updateRoundsFromApi = (nextRounds: unknown[][]) => {
    if (!Array.isArray(nextRounds)) return;
    const mergedRounds = mergeChallengeRoundsPreservingLocalScores(
      nextRounds,
      rounds,
    );
    setChallengeRoundsJson(JSON.stringify(mergedRounds, null, 2));
    setChallengeDetail((current) =>
      current
        ? {
            ...current,
            rounds: mergedRounds as ChallengeDetail["rounds"],
          }
        : current,
    );
  };

  const updateRoundStatusLocally = (
    roundIndex: number,
    matchIndex: number,
    status: "pending" | "scheduled" | "in_progress",
  ) => {
    const nextRounds = (Array.isArray(rounds) ? rounds : []).map(
      (round: any[], currentRoundIndex: number) =>
        Array.isArray(round)
          ? round.map((match: any, currentMatchIndex: number) =>
              currentRoundIndex === roundIndex &&
              currentMatchIndex === matchIndex
                ? { ...match, status }
                : match,
            )
          : round,
    );
    setChallengeRoundsJson(JSON.stringify(nextRounds, null, 2));
    setChallengeDetail((current) =>
      current
        ? {
            ...current,
            rounds: nextRounds as ChallengeDetail["rounds"],
          }
        : current,
    );
  };

  const toggleCourtStatus = async (
    roundIndex: number,
    matchIndex: number,
    match: any,
    status: "pending" | "scheduled" | "in_progress",
  ) => {
    const matchId = Number(match?.id || 0);
    if (pendingCourtMatchId) return;
    if (!matchId) {
      updateRoundStatusLocally(roundIndex, matchIndex, status);
      onSuccess(
        status === "in_progress"
          ? "Partida colocada em quadra."
          : "Partida retirada da quadra.",
      );
      return;
    }
    setPendingCourtMatchId(matchId);
    try {
      const response = await updateChallengeMatchStatus(
        token,
        Number(challengeDetail.id),
        matchId,
        status,
      );
      updateRoundsFromApi(response.rounds || []);
      onSuccess(
        status === "in_progress"
          ? "Partida colocada em quadra."
          : "Partida retirada da quadra.",
      );
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : siteTexts.challengeEngine.courtStatus.updateError,
      );
    } finally {
      setPendingCourtMatchId(null);
    }
  };

  const saveOrganizerLogo = async (logoUrl: string) => {
    if (!token || !canManageOrganizerLogo) {
      onError(t.createChallengePage.eventIdentity.logoMissing);
      return;
    }
    setOrganizerLogoSaving(true);
    try {
      const currentSettings = await getOrganizerVisualIdentity(token);
      const nextSettings = await saveOrganizerVisualIdentity(
        token,
        currentSettings.isCustom
          ? {
              mode: "custom",
              colors: currentSettings.colors,
              logoUrl,
            }
          : {
              mode: "tnp_standard",
              logoUrl,
            },
      );
      setChallengeDetail((current) =>
        current
          ? {
              ...current,
              eventIdentityMode: "organizer_logo",
              organizerLogoUrl: nextSettings.logoUrl || null,
            }
          : current,
      );
      onSuccess(t.createChallengePage.eventIdentity.logoSaved);
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : siteTexts.profile.user.visualIdentity.saveError,
      );
    } finally {
      setOrganizerLogoSaving(false);
    }
  };

  const editorTitle = isTournamentChallenge
    ? t.createChallengePage.editTournamentTitle
    : t.createChallengePage.editChallengeTitle;
  const editorSubtitle = isTournamentChallenge
    ? t.createChallengePage.editSubtitleTournament
    : t.createChallengePage.editSubtitleChallenge;
  const liveSummaryLabel = isTournamentChallenge
    ? t.createChallengePage.summary.tournamentSummary
    : t.createChallengePage.summary.challengeSummary;
  const summaryDate = challengeScheduledAtDraft
    ? challengeScheduledAtDraft.toLocaleDateString("pt-BR")
    : t.createChallengePage.summary.toBeDefined;
  const summaryLocation = [challengeDetail.city, challengeDetail.address]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" / ");
  const summaryCategories = hasRegistrationCategories
    ? `${challengeTournamentCategories.length} categoria${
        challengeTournamentCategories.length === 1 ? "" : "s"
      }`
    : challengeDetail.categoryGender === "mixed" ||
        challengeDetail.categoryGender === "open"
      ? [challengeDetail.categoryMale, challengeDetail.categoryFemale]
          .filter(Boolean)
          .join(" / ") || "A definir"
      : challengeDetail.category || "A definir";
  const summaryCards = [
    {
      icon: <AwardIcon aria-hidden="true" />,
      label: "Evento",
      value: challengeDetail.challengeName || editorTitle,
    },
    {
      icon: <CalendarIcon aria-hidden="true" />,
      label: "Data",
      value: summaryDate,
    },
    {
      icon: <MapPinIcon aria-hidden="true" />,
      label: "Cidade/local",
      value: summaryLocation || "A definir",
    },
    {
      icon: <TagIcon aria-hidden="true" />,
      label: "Categorias",
      value: summaryCategories,
    },
  ];
  const onToggleEditSection = (id: string) => {
    setActiveEditSection((current) => (current === id ? "" : id));
  };
  const editorSectionMode = "modal";
  const generalStatus =
    challengeDetail.challengeName &&
    (String(challengeEventDraft.amountPerPlayer || "").trim() ||
      Number.isFinite(Number(challengeEventDraft.savedAmountPerPlayerCents)))
      ? "complete"
      : "pending";
  const locationStatus =
    challengeDetail.city && challengeDetail.address && challengeScheduledAtDraft
      ? "complete"
      : "pending";
  const participantsStatus =
    hasRegistrationCategories
      ? challengeTournamentCategories.length > 0
        ? "complete"
        : "pending"
      : challengeSelectedParticipantsCount > 0
        ? "complete"
        : "attention";
  const hasOnCourtMatch = rounds.some((round: any[]) =>
    Array.isArray(round)
      ? round.some(
          (match: any) => normalizeChallengeMatchStatus(match) === "in_progress",
        )
      : false,
  );
  const roundsStatus = hasOnCourtMatch
    ? "in_progress"
    : rounds.length > 0
      ? "complete"
      : "pending";
  const activeRoundsCategoryKey = getActiveRoundsCategoryKey(challengeTournamentCategories, activeTournamentCategoryKey);
  const categoryRoundEntries = useMemo(
    () => groupCategoryRounds<AdminRoundMatch>(rounds, challengeTournamentCategories),
    [challengeTournamentCategories, rounds],
  );
  const eventSettingsStatus =
    String(challengeEventDraft.imageUrl || "").trim()
      ? "complete"
      : "pending";
  const tournamentScheduleStatus = tournamentPendingChangeItems.some((item) =>
    item.includes(t.createChallengePage.summary.pendingSchedule),
  )
    ? "attention"
    : "complete";
  const tournamentOperationsStatus =
    tournamentPendingChangeItems.length > 0
      ? "attention"
      : Number(challengeDetail.tournamentBracketMatchesCount || 0) > 0
        ? "complete"
        : "pending";
  const eventIdentityMode: EventIdentityMode =
    !challengeDetail.isOfficial &&
    challengeDetail.eventIdentityMode === "tnp_standard"
      ? "organizer_photo"
      : challengeDetail.eventIdentityMode ||
        (challengeDetail.isOfficial ? "tnp_standard" : "organizer_photo");
  const editSummaryDateTime = challengeScheduledAtDraft
    ? `${challengeScheduledAtDraft.toLocaleDateString("pt-BR")} às ${String(
        challengeScheduledAtDraft.getHours(),
      ).padStart(2, "0")}:${String(challengeScheduledAtDraft.getMinutes()).padStart(
        2,
        "0",
      )}`
    : "A definir";
  const currentEditorSnapshot = useMemo<EditorSnapshot>(
    () => ({
      general: {
        address: challengeDetail.address || "",
        challengeModel: challengeDetail.challengeModel || "tnp_standard",
        challengeName: challengeDetail.challengeName || "",
        city: challengeDetail.city || "",
        courtsCount: challengeDetail.courtsCount || null,
        description: challengeDetail.description || "",
        durationDraft: challengeDurationDraft || "",
        eventDraft: {
          amountPerPlayer: challengeEventDraft.amountPerPlayer || "",
          eventImageUrl: challengeEventDraft.imageUrl || "",
          registrationType: challengeEventDraft.registrationType || "",
          registrationsOpen: Boolean(challengeEventDraft.registrationsOpen),
          availabilityRestrictionAmount:
            challengeEventDraft.availabilityRestrictionAmount || "",
          availabilityRestrictionDeadlineAt:
            challengeEventDraft.availabilityRestrictionDeadlineAt || "",
          availabilityRestrictionLimit:
            challengeEventDraft.availabilityRestrictionLimit || "",
          availabilityRestrictionPeriodsText:
            challengeEventDraft.availabilityRestrictionPeriodsText || "",
          availabilityRestrictionTypes:
            challengeEventDraft.availabilityRestrictionTypes || [],
          availabilityRestrictionsEnabled: Boolean(
            challengeEventDraft.availabilityRestrictionsEnabled,
          ),
        },
        eventIdentityMode: challengeDetail.eventIdentityMode || "",
        gameCount: challengeDetail.gameCount || null,
        gameDurationMinutes: challengeDetail.gameDurationMinutes || null,
        gameType: challengeDetail.gameType || "",
        groupName: challengeDetail.groupName || "",
        includeInRanking: Boolean(challengeDetail.includeInRanking ?? true),
        isOfficial: Boolean(challengeDetail.isOfficial ?? true),
        organizerUserId: challengeDetail.organizerUserId || null,
        playersCount: challengeDetail.playersCount || null,
        scheduledAt: challengeScheduledAtDraft?.toISOString() || null,
        scheduledEndAt: challengeScheduledEndAtDraft?.toISOString() || null,
        state: challengeDetail.state || "",
      },
      shirts: {
        shirtAvailabilityType: challengeEventDraft.shirtAvailabilityType || "none",
        shirtDeadlineAt: challengeEventDraft.shirtDeadlineAt || "",
        shirtQuantityLimit: challengeEventDraft.shirtQuantityLimit || "",
        shirtSizes: challengeEventDraft.shirtSizes || [],
        shirtSizesEnabled: Boolean(challengeEventDraft.shirtSizesEnabled),
      },
      participants: {
        category: challengeDetail.category || null,
        categoryFemale: challengeDetail.categoryFemale || null,
        categoryGender: challengeDetail.categoryGender || null,
        categoryMale: challengeDetail.categoryMale || null,
        draftPairs: hasRegistrationCategories
          ? []
          : draftPairs.map((pair) => ({
              id: pair.id,
              cpf1: pair.cpf1 || "",
              cpf2: pair.cpf2 || "",
            })),
        draftPlayers: hasRegistrationCategories
          ? []
          : draftPlayers.map((player) => ({
              cpf: player.cpf,
              side: player.side || "",
            })),
        pairMode,
        registrationCategories: challengeTournamentCategories
          .map((category) => ({
            address: category.address || "",
            categoryFemale: category.categoryFemale || "",
            categoryGender: category.categoryGender || "",
            categoryKey: category.categoryKey || "",
            categoryMale: category.categoryMale || "",
            categoryName: category.categoryName || "",
            registrationType: category.registrationType || "",
            scheduledAt: category.scheduledAt || "",
            scheduledEndAt: category.scheduledEndAt || "",
            shirtSizes: category.shirtSizes || [],
            shirtSizesEnabled: Boolean(category.shirtSizesEnabled),
            slotLimit: Number(category.slotLimit || 0),
          }))
          .sort((left, right) =>
            String(left.categoryKey).localeCompare(String(right.categoryKey)),
          ),
        registrationType: challengeDetail.registrationType || "",
        slotLimit: challengeDetail.slotLimit || null,
      },
      rounds: isTournamentChallenge ? null : normalizeEditorRoundsSnapshot(rounds),
    }),
    [
      challengeDetail.address,
      challengeDetail.category,
      challengeDetail.categoryFemale,
      challengeDetail.categoryGender,
      challengeDetail.categoryMale,
      challengeDetail.challengeModel,
      challengeDetail.challengeName,
      challengeDetail.city,
      challengeDetail.courtsCount,
      challengeDetail.description,
      challengeDetail.eventIdentityMode,
      challengeDetail.gameCount,
      challengeDetail.gameDurationMinutes,
      challengeDetail.gameType,
      challengeDetail.groupName,
      challengeDetail.includeInRanking,
      challengeDetail.isOfficial,
      challengeDetail.organizerUserId,
      challengeDetail.playersCount,
      challengeDetail.registrationType,
      challengeDetail.slotLimit,
      challengeDetail.state,
      challengeDurationDraft,
      challengeEventDraft.amountPerPlayer,
      challengeEventDraft.imageUrl,
      challengeEventDraft.registrationType,
      challengeEventDraft.registrationsOpen,
      challengeEventDraft.shirtAvailabilityType,
      challengeEventDraft.shirtDeadlineAt,
      challengeEventDraft.shirtQuantityLimit,
      challengeEventDraft.shirtSizes,
      challengeEventDraft.shirtSizesEnabled,
      challengeEventDraft.availabilityRestrictionAmount,
      challengeEventDraft.availabilityRestrictionDeadlineAt,
      challengeEventDraft.availabilityRestrictionLimit,
      challengeEventDraft.availabilityRestrictionPeriodsText,
      challengeEventDraft.availabilityRestrictionTypes,
      challengeEventDraft.availabilityRestrictionsEnabled,
      challengeScheduledAtDraft,
      challengeScheduledEndAtDraft,
      challengeTournamentCategories,
      draftPairs,
      draftPlayers,
      hasRegistrationCategories,
      isTournamentChallenge,
      pairMode,
      rounds,
    ],
  );
  const editorRoundsReadyForBaseline =
    isTournamentChallenge ||
    !Array.isArray(challengeDetail.rounds) ||
    challengeDetail.rounds.length === 0 ||
    rounds.length > 0;
  useEffect(() => {
    if (lastBaselineChallengeIdRef.current === challengeDetail.id) return;
    if (challengeEventRegistrationsLoading) return;
    if (!editorRoundsReadyForBaseline) return;
    lastBaselineChallengeIdRef.current = challengeDetail.id;
    setEditorBaselineSnapshot(currentEditorSnapshot);
    setTournamentPendingChangeItems([]);
    setTournamentEventSchedulePendingChangeItems([]);
  }, [
    challengeDetail.id,
    challengeEventRegistrationsLoading,
    currentEditorSnapshot,
    editorRoundsReadyForBaseline,
  ]);
  useEffect(() => {
    if (baselineRefreshRequest <= 0) return;
    setEditorBaselineSnapshot(currentEditorSnapshot);
    setTournamentPendingChangeItems([]);
    setTournamentEventSchedulePendingChangeItems([]);
    setBaselineRefreshRequest(0);
  }, [baselineRefreshRequest, currentEditorSnapshot]);
  useEffect(() => {
    if (lastCategorySaveRevisionRef.current === categorySaveRevision) return;
    lastCategorySaveRevisionRef.current = categorySaveRevision;
    if (categorySaveRevision <= 0) return;
    setEditorBaselineSnapshot(currentEditorSnapshot);
    setTournamentPendingChangeItems([]);
    setTournamentEventSchedulePendingChangeItems([]);
  }, [categorySaveRevision, currentEditorSnapshot]);

  const challengeEditorChangeItems = useMemo(() => {
    if (!editorBaselineSnapshot) return [];
    const items: string[] = [];
    if (
      !sameSnapshotBlock(
        editorBaselineSnapshot.general,
        currentEditorSnapshot.general,
      )
    ) {
      items.push(t.createChallengePage.summary.pendingGeneralInfo);
    }
    if (
      !sameSnapshotBlock(
        editorBaselineSnapshot.participants,
        currentEditorSnapshot.participants,
      )
    ) {
      items.push(t.createChallengePage.summary.pendingParticipants);
    }
    if (
      !sameSnapshotBlock(
        editorBaselineSnapshot.shirts,
        currentEditorSnapshot.shirts,
      )
    ) {
      items.push(t.createChallengePage.summary.pendingShirts);
    }
    if (
      !sameSnapshotBlock(
        editorBaselineSnapshot.rounds,
        currentEditorSnapshot.rounds,
      )
    ) {
      items.push(t.createChallengePage.summary.pendingRounds);
    }
    return items;
  }, [
    currentEditorSnapshot,
    editorBaselineSnapshot,
    t.createChallengePage.summary.pendingGeneralInfo,
    t.createChallengePage.summary.pendingParticipants,
    t.createChallengePage.summary.pendingRounds,
    t.createChallengePage.summary.pendingShirts,
  ]);
  const editorChangeItems = useMemo(() => {
    return [
      ...challengeEditorChangeItems,
      ...tournamentPendingChangeItems,
      ...tournamentEventSchedulePendingChangeItems,
    ];
  }, [
    challengeEditorChangeItems,
    tournamentEventSchedulePendingChangeItems,
    tournamentPendingChangeItems,
  ]);
  const hasEditorChanges = editorChangeItems.length > 0;
  const hasEnabledShirtConfiguration =
    (Boolean(challengeEventDraft.shirtSizesEnabled) &&
      Array.isArray(challengeEventDraft.shirtSizes) &&
      challengeEventDraft.shirtSizes.length > 0) ||
    challengeTournamentCategories.some(
      (category) =>
        Boolean(category.shirtSizesEnabled) &&
        Array.isArray(category.shirtSizes) &&
        category.shirtSizes.length > 0,
    );
  const registrationsAreOpen = Boolean(challengeEventDraft.registrationsOpen);
  const finishedChallengeReadOnly =
    !isTournamentChallenge &&
    !(isPoco && useCategoryRoundsAccordion && challengeTournamentCategories.some(category => !categoryRoundEntries.get(category.categoryKey)?.length)) &&
    (challengeDetail.status === "finished" ||
      Boolean(challengeDetail.finishedAt));
  const competitionLockedForStructure =
    Boolean(challengeDetail.startedAt) ||
    challengeDetail.status === "started" ||
    challengeDetail.status === "finished" ||
    Boolean(challengeDetail.finishedAt);
  const canAddParticipantsByFlow =
    (registrationsAreOpen && !competitionLockedForStructure) ||
    (isPoco && useCategoryRoundsAccordion && !finishedChallengeReadOnly &&
      !(categoryRoundEntries.get(activeRoundsCategoryKey) || []).some(entry => entry.round.some(match =>
        [match.score1, match.score2].some(score => score !== '' && score != null))));
  const canRunPairDrawByFlow =
    !registrationsAreOpen && !competitionLockedForStructure;
  const canGenerateUnstartedCategory = canGenerateMissingPocoCategory({
    isPoco, usesCategories: useCategoryRoundsAccordion, categoryKey: activeRoundsCategoryKey,
    hasRounds: Boolean(categoryRoundEntries.get(activeRoundsCategoryKey)?.length),
    finished: finishedChallengeReadOnly,
  });
  const canGenerateRoundsByFlow =
    !registrationsAreOpen && (!competitionLockedForStructure || canGenerateUnstartedCategory);
  const hasGeneratedRounds = rounds.some(
    (round) => Array.isArray(round) && round.length > 0,
  );
  const isPendingChallenge = challengeDetail.status === "pending";
  const showPairDrawAction =
    !isTournamentChallenge &&
    isPendingChallenge &&
    !isSuper8 &&
    currentPairMode !== "fixed";
  const showGenerateRoundsAction =
    !isTournamentChallenge &&
    (isPendingChallenge || canGenerateUnstartedCategory) &&
    (isSuper8 || currentPairMode === "fixed" || draftPairs.length > 0);
  const canGenerateRoundsFromSummary =
    canGenerateRoundsByFlow &&
    (isSuper8
      ? canGeneratePlayerBasedRounds
      : canGenerateFixedPairRounds);
  const canStartFromSummary =
    canStartChallenge && !registrationsAreOpen && hasGeneratedRounds;
  const runStartChallenge = async () => {
    if (!canStartFromSummary || startingChallenge) return;
    setStartingChallenge(true);
    try {
      await onStartChallenge();
    } finally {
      setStartingChallenge(false);
    }
  };
  const runToggleRegistrations = async () => {
    await onToggleRegistrations();
    setConfirmCloseRegistrations(false);
  };
  const participantNotice = resolveAdminParticipantNotice({
    competitionLocked: competitionLockedForStructure,
    currentPairMode,
    draftPairsCount: draftPairs.length,
    draftPlayersCount: draftPlayers.length,
    isSuper8,
    maxDrawPlayers: maxDrawPlayersForCurrentChallenge,
    maxFixedPairs: maxFixedPairsForCurrentChallenge,
    registrationsOpen: registrationsAreOpen,
    super8MainPlayersCount: super8GroupAPlayers.length,
    super8WaitingPlayersCount: super8WaitingPlayers.length,
  });
  const requiredMissingItems = useMemo(() => {
    const items: string[] = [];
    if (isRequiredTextMissing(challengeDetail.challengeName)) {
      items.push("nome do evento");
    }
    if (isRequiredTextMissing(challengeDetail.city)) {
      items.push("cidade do evento");
    }
    if (isRequiredTextMissing(challengeDetail.address)) {
      items.push("local/clube");
    }
    if (isRequiredTextMissing(challengeEventDraft.imageUrl)) {
      items.push("imagem do evento");
    }
    if (
      !String(challengeEventDraft.amountPerPlayer || "").trim() &&
      !Number.isFinite(Number(challengeEventDraft.savedAmountPerPlayerCents))
    ) {
      items.push("valor por jogador");
    }
    if (
      isTournamentChallenge &&
      challengeTournamentCategories.length === 0
    ) {
      items.push("ao menos uma categoria");
    }
    if (
      challengeEventDraft.shirtSizesEnabled &&
      (!Array.isArray(challengeEventDraft.shirtSizes) ||
        challengeEventDraft.shirtSizes.length === 0)
    ) {
      items.push("tamanhos de camiseta");
    }
    if (
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "period" &&
      !String(challengeEventDraft.shirtDeadlineAt || "").trim()
    ) {
      items.push("prazo das camisetas");
    }
    if (
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "quantity" &&
      !String(challengeEventDraft.shirtQuantityLimit || "").trim()
    ) {
      items.push("limite de camisetas");
    }
    if (challengeDetail.challengeModel === "custom") {
      if (
        challengeDetail.challengeType !== "super8" &&
        (!Number.isFinite(Number(challengeDetail.courtsCount)) ||
          Number(challengeDetail.courtsCount) <= 0)
      ) {
        items.push("quantidade de quadras");
      }
      if (
        challengeDetail.challengeType !== "super8" &&
        challengeDetail.challengeType !== "poco" &&
        (!Number.isFinite(Number(challengeDetail.playersCount)) ||
          Number(challengeDetail.playersCount) <= 0)
      ) {
        items.push("quantidade de jogadores");
      }
      if (
        challengeDetail.gameType === "time" &&
        (!Number.isFinite(Number(challengeDetail.gameDurationMinutes)) ||
          Number(challengeDetail.gameDurationMinutes) <= 0)
      ) {
        items.push("tempo de jogo");
      }
      if (
        challengeDetail.gameType !== "time" &&
        (!Number.isFinite(Number(challengeDetail.gameCount)) ||
          Number(challengeDetail.gameCount) <= 0)
      ) {
        items.push("quantidade de games");
      }
    }
    return items;
  }, [
    challengeDetail.address,
    challengeDetail.challengeModel,
    challengeDetail.challengeName,
    challengeDetail.challengeType,
    challengeDetail.city,
    challengeDetail.courtsCount,
    challengeDetail.gameCount,
    challengeDetail.gameDurationMinutes,
    challengeDetail.gameType,
    challengeDetail.playersCount,
    challengeEventDraft.amountPerPlayer,
    challengeEventDraft.imageUrl,
    challengeEventDraft.savedAmountPerPlayerCents,
    challengeEventDraft.shirtAvailabilityType,
    challengeEventDraft.shirtDeadlineAt,
    challengeEventDraft.shirtQuantityLimit,
    challengeEventDraft.shirtSizes,
    challengeEventDraft.shirtSizesEnabled,
    challengeTournamentCategories.length,
    isTournamentChallenge,
  ]);

  const saveAllChanges = async () => {
    if (!hasEditorChanges || savingChallenge) return false;
    const savedTournamentEventSchedule =
      (await tournamentEventScheduleRef.current?.savePendingChanges()) ?? true;
    if (!savedTournamentEventSchedule) return false;
    const savedTournamentOperations =
      (await tournamentOperationsRef.current?.savePendingChanges()) ?? true;
    if (!savedTournamentOperations) return false;
    if (challengeEditorChangeItems.length === 0) {
      setBaselineRefreshRequest((current) => current + 1);
      return true;
    }
    const shouldIncludeRounds =
      (finishedChallengeReadOnly || !registrationsAreOpen) &&
      challengeEditorChangeItems.includes(
        t.createChallengePage.summary.pendingRounds,
      );
    const shouldSaveRoundsOnly =
      shouldIncludeRounds &&
      challengeEditorChangeItems.every(
        (item) => item === t.createChallengePage.summary.pendingRounds,
      );
    const saved = await onSaveChallenge({
      includeRounds: shouldIncludeRounds,
      roundsOnly: shouldSaveRoundsOnly,
    });
    if (saved !== false) {
      setBaselineRefreshRequest((current) => current + 1);
      return true;
    }
    return false;
  };
  const hasRoundsChanges = challengeEditorChangeItems.includes(
    t.createChallengePage.summary.pendingRounds,
  );
  const saveRoundsChanges = async () => {
    if (!hasRoundsChanges || savingChallenge) return false;
    const saved = await onSaveChallenge({
      includeRounds: true,
      roundsOnly: true,
    });
    if (saved !== false) {
      const hasOnlyRoundsChanges = challengeEditorChangeItems.every(
        (item) => item === t.createChallengePage.summary.pendingRounds,
      );
      if (hasOnlyRoundsChanges) {
        setBaselineRefreshRequest((current) => current + 1);
      }
      return true;
    }
    return false;
  };
  const runGenerationAction = async (
    action: () => boolean | void | Promise<boolean | void>,
  ) => {
    const saved = await action();
    if (saved !== false) {
      setBaselineRefreshRequest((current) => current + 1);
    }
  };

  return (
    <div className="admin-event-form-dashboard admin-event-editor-dashboard">
      <ConfirmModal
        open={confirmCloseRegistrations}
        title={operationTexts.closeRegistrations}
        message={operationTexts.closeRegistrationsMessage.replace(
          "{name}",
          challengeDetail.challengeName || editorTitle,
        )}
        confirmLabel={operationTexts.closeRegistrations}
        cancelLabel={siteTexts.common.cancel}
        loading={registrationsToggleLoading}
        loadingLabel={operationTexts.closingRegistrations}
        onCancel={() => {
          if (!registrationsToggleLoading) setConfirmCloseRegistrations(false);
        }}
        onConfirm={() => void runToggleRegistrations()}
      />
      {restrictionModalRegistration && (
        <AdminAvailabilityRestrictionModal
          registration={restrictionModalRegistration}
          saving={restrictionSaving}
          canRemove={isAdmin}
          onClose={() => setRestrictionModalRegistration(null)}
          onSubmit={async (payload) => {
            setRestrictionSaving(true);
            try {
              const result = await onSaveAvailabilityRestriction(
                restrictionModalRegistration.id,
                payload,
              );
              if (payload.mode !== "impact") {
                setRestrictionModalRegistration(null);
                onLoadChallengeEventRegistrations(
                  manualRegistrationEventIdForChallenge,
                );
              }
              return result;
            } finally {
              setRestrictionSaving(false);
            }
          }}
        />
      )}
      <div className="admin-event-form-hero">
        <div className="admin-event-form-hero-copy">
          <span className="admin-event-form-hero-icon" aria-hidden="true">
            <EditIcon />
          </span>
          <div>
            <h2>{editorTitle}</h2>
            <p>{editorSubtitle}</p>
          </div>
        </div>
      </div>

      <div className="admin-event-form-summary-grid" aria-label="Resumo do evento">
        {summaryCards.map((card) => (
          <div className="admin-event-form-summary-card" key={card.label}>
            <span>{card.icon}</span>
            <div>
              <strong>{card.value}</strong>
              <small>{card.label}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-event-form-layout admin-event-editor-layout">
        <div className="admin-event-form-main">
        <EditorAccordionSection
          id="general"
          isOpen={activeEditSection === "general"}
          onToggle={onToggleEditSection}
          mode={editorSectionMode}
          readOnly={finishedChallengeReadOnly}
          onSave={saveAllChanges}
          canSave={hasEditorChanges}
          saving={savingChallenge}
          icon={<EditIcon />}
          status={generalStatus}
          title={t.createChallengePage.generalInfoTitle}
          subtitle={t.createChallengePage.generalRequiredDescription}
        >
          <div
            className={`form-grid admin-edit-form-grid${
              isTournamentChallenge ? " is-tournament" : ""
            }`}
          >
        {isTournamentChallenge ? (
          <>
            <label className="field">
              <span>{t.labels.challengeName}</span>
              <input
                type="text"
                value={challengeDetail.challengeName}
                onChange={(event) =>
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, challengeName: event.target.value } : prev,
                  )
                }
              />
            </label>
            <label className="field">
              <span>{t.labels.description}</span>
              <textarea
                rows={4}
                value={challengeDetail.description || ""}
                onChange={(event) =>
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, description: event.target.value } : prev,
                  )
                }
              />
            </label>
          </>
        ) : (
          <AdminChallengeGeneralForm
            categoryOptions={categoryOptions}
            challengeDetail={challengeDetail}
            challengeDurationDraft={challengeDurationDraft}
            challengeScheduledAtDraft={challengeScheduledAtDraft}
            challengeScheduledEndAtDraft={challengeScheduledEndAtDraft}
            genderOptions={genderOptions}
            isAdmin={isAdmin}
            isTournamentChallenge={isTournamentChallenge}
            organizerUsers={organizerUsers}
            setChallengeDetail={setChallengeDetail}
            setChallengeDurationDraft={setChallengeDurationDraft}
            setChallengeScheduledAtDraft={setChallengeScheduledAtDraft}
            setChallengeScheduledEndAtDraft={setChallengeScheduledEndAtDraft}
            showLocationFields={false}
            showScheduleFields={false}
          />
        )}
        <AdminChallengeEventAmountField
          challengeEventDraft={challengeEventDraft}
          setChallengeEventDraft={setChallengeEventDraft}
        />
          </div>
        </EditorAccordionSection>
        <EditorAccordionSection
          id="location"
          isOpen={activeEditSection === "location"}
          onToggle={onToggleEditSection}
          mode={editorSectionMode}
          readOnly={finishedChallengeReadOnly}
          onSave={saveAllChanges}
          canSave={hasEditorChanges}
          saving={savingChallenge}
          icon={<MapPinIcon />}
          status={locationStatus}
          title={t.createChallengePage.locationScheduleTitle}
          subtitle={t.createChallengePage.locationScheduleDescription}
        >
          <div
            className={`form-grid admin-edit-form-grid${
              isTournamentChallenge ? " is-tournament" : ""
            }`}
          >
            <AdminVenueFields
              token={token}
              address={challengeDetail.address || ""}
              city={challengeDetail.city || ""}
              state={challengeDetail.state || ""}
              onAddressChange={(value) =>
                setChallengeDetail((prev) =>
                  prev ? { ...prev, address: value } : prev,
                )
              }
              onCityChange={(value) =>
                setChallengeDetail((prev) =>
                  prev ? { ...prev, city: value } : prev,
                )
              }
              onStateChange={(value) =>
                setChallengeDetail((prev) =>
                  prev ? { ...prev, state: value } : prev,
                )
              }
            />
            {isTournamentChallenge ? (
              <>
                <div className="field">
                  <span>{t.createChallengePage.tournamentStart}</span>
                  <OrganizerDatePicker
                    label={t.createChallengePage.tournamentStart}
                    value={
                      challengeScheduledAtDraft
                        ? formatDateInputValue(challengeScheduledAtDraft)
                        : ""
                    }
                    disabledAfter={
                      challengeScheduledEndAtDraft
                        ? formatDateInputValue(challengeScheduledEndAtDraft)
                        : undefined
                    }
                    onChange={(value) => {
                      const nextDate = parseDateInputValue(value);
                      if (!nextDate) {
                        setChallengeScheduledAtDraft(null);
                        setChallengeDetail((prev) =>
                          prev ? { ...prev, scheduledAt: null } : prev,
                        );
                        return;
                      }
                      const normalized = extractDatePart(nextDate);
                      setChallengeScheduledAtDraft(normalized);
                      setChallengeDetail((prev) =>
                        prev
                          ? { ...prev, scheduledAt: normalized.toISOString() }
                          : prev,
                      );
                    }}
                  />
                </div>
                <div className="field">
                  <span>{t.createChallengePage.tournamentEnd}</span>
                  <OrganizerDatePicker
                    label={t.createChallengePage.tournamentEnd}
                    value={
                      challengeScheduledEndAtDraft
                        ? formatDateInputValue(challengeScheduledEndAtDraft)
                        : ""
                    }
                    disabledBefore={
                      challengeScheduledAtDraft
                        ? formatDateInputValue(challengeScheduledAtDraft)
                        : undefined
                    }
                    onChange={(value) => {
                      const nextDate = parseDateInputValue(value);
                      if (!nextDate) {
                        setChallengeScheduledEndAtDraft(null);
                        setChallengeDetail((prev) =>
                          prev ? { ...prev, scheduledEndAt: null } : prev,
                        );
                        return;
                      }
                      const normalized = extractDatePart(nextDate);
                      setChallengeScheduledEndAtDraft(normalized);
                      setChallengeDetail((prev) =>
                        prev
                          ? { ...prev, scheduledEndAt: normalized.toISOString() }
                          : prev,
                      );
                    }}
                  />
                </div>
                <div
                  id={tournamentScheduleSettingsHostId}
                  className="admin-tournament-schedule-settings-host"
                />
              </>
            ) : (
              <AdminChallengeScheduleFields
                challengeDetail={challengeDetail}
                challengeDurationDraft={challengeDurationDraft}
                challengeScheduledAtDraft={challengeScheduledAtDraft}
                challengeScheduledEndAtDraft={challengeScheduledEndAtDraft}
                isTournamentChallenge={isTournamentChallenge}
                setChallengeDetail={setChallengeDetail}
                setChallengeDurationDraft={setChallengeDurationDraft}
                setChallengeScheduledAtDraft={setChallengeScheduledAtDraft}
                setChallengeScheduledEndAtDraft={setChallengeScheduledEndAtDraft}
              />
            )}
          </div>
        </EditorAccordionSection>
        {isTournamentOperations && (
          <EditorAccordionSection
            id="courts"
            isOpen={activeEditSection === "courts"}
            onToggle={onToggleEditSection}
            mode={editorSectionMode}
            onSave={saveAllChanges}
            canSave={hasEditorChanges}
            saving={savingChallenge}
            icon={<CalendarIcon />}
            status={tournamentScheduleStatus}
            title={t.createChallengePage.courtsTitle}
            subtitle={t.createChallengePage.courtsDescription}
          >
            <div
              id={tournamentCourtSettingsHostId}
              className="admin-tournament-schedule-settings-host"
            />
          </EditorAccordionSection>
        )}
        {isTournamentChallenge && isAdmin && (
          <EditorAccordionSection
            id="organization"
            isOpen={activeEditSection === "organization"}
            onToggle={onToggleEditSection}
            mode={editorSectionMode}
            onSave={saveAllChanges}
            canSave={hasEditorChanges}
            saving={savingChallenge}
            icon={<AwardIcon />}
            status="complete"
            title={t.createChallengePage.organizationRankingTitle}
            subtitle={t.createChallengePage.organizationRankingDescription}
          >
            <div className="form-grid admin-edit-form-grid is-tournament">
              <div className="admin-row-two admin-form-block-organization">
                <label className="field">
                  <span>{t.createChallengePage.organization}</span>
                  <select
                    value={
                      Boolean(challengeDetail.isOfficial ?? true)
                        ? "official"
                        : "organizer"
                    }
                    onChange={(event) => {
                      const next = event.target.value;
                      setChallengeDetail((prev) =>
                        prev
                          ? {
                              ...prev,
                              isOfficial: next === "official",
                              organizerUserId:
                                next === "official"
                                  ? null
                                  : (prev.organizerUserId ?? null),
                            }
                          : prev,
                      );
                    }}
                  >
                    <option value="official">{t.createChallengePage.official}</option>
                    <option value="organizer">{t.createChallengePage.organizer}</option>
                  </select>
                </label>
                <label className="field">
                  <span>{t.createChallengePage.organizer}</span>
                  <select
                    value={String(challengeDetail.organizerUserId ?? "")}
                    onChange={(event) => {
                      const parsed = Number(event.target.value);
                      setChallengeDetail((prev) =>
                        prev
                          ? {
                              ...prev,
                              organizerUserId: Number.isFinite(parsed)
                                ? Math.floor(parsed)
                                : null,
                            }
                          : prev,
                      );
                    }}
                    disabled={Boolean(challengeDetail.isOfficial ?? true)}
                  >
                    <option value="">
                      {Boolean(challengeDetail.isOfficial ?? true)
                        ? t.createChallengePage.emptyOption
                        : t.createChallengePage.select}
                    </option>
                    {organizerUsers.map((user) => (
                      <option key={`organizer-edit-${user.id}`} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="field">
                <span>
                  {Boolean(challengeDetail.isOfficial ?? true)
                    ? t.createChallengePage.includeOfficialRanking
                    : t.createChallengePage.includeOrganizerRanking}
                </span>
                <select
                  value={Boolean(challengeDetail.includeInRanking ?? true) ? "yes" : "no"}
                  onChange={(event) => {
                    const next = event.target.value === "yes";
                    setChallengeDetail((prev) =>
                      prev ? { ...prev, includeInRanking: next } : prev,
                    );
                  }}
                >
                  <option value="yes">{t.createChallengePage.yes}</option>
                  <option value="no">{t.createChallengePage.no}</option>
                </select>
              </label>
            </div>
          </EditorAccordionSection>
        )}
        <EditorAccordionSection
          id="event-settings"
          isOpen={activeEditSection === "event-settings"}
          onToggle={onToggleEditSection}
          mode={editorSectionMode}
          readOnly={finishedChallengeReadOnly}
          onSave={saveAllChanges}
          canSave={hasEditorChanges}
          saving={savingChallenge}
          icon={<CalendarIcon />}
          status={eventSettingsStatus}
          title={t.createChallengePage.visualIdentityTitle}
          subtitle={t.createChallengePage.visualIdentityDescription}
        >
          <div className="admin-event-settings-combo">
            <AdminChallengeEventImageField
              challengeEventDraft={challengeEventDraft}
              setChallengeEventDraft={setChallengeEventDraft}
            />
            <AdminEventIdentityFields
              value={eventIdentityMode}
              onChange={(mode) =>
                setChallengeDetail((prev) =>
                  prev ? { ...prev, eventIdentityMode: mode } : prev,
                )
              }
              organizerName={challengeDetail.organizerName}
              organizerAvatarUrl={challengeDetail.organizerAvatarUrl}
              organizerLogoUrl={challengeDetail.organizerLogoUrl}
              disabled={Boolean(challengeDetail.isOfficial)}
              allowTnpStandard={Boolean(isAdmin && challengeDetail.isOfficial)}
              canManageLogo={canManageOrganizerLogo}
              logoSaving={organizerLogoSaving}
              onLogoError={onError}
              onLogoUpload={saveOrganizerLogo}
            />
          </div>
        </EditorAccordionSection>
        {isTournamentChallenge && (
          <EditorAccordionSection
            id="advanced"
            isOpen={activeEditSection === "advanced"}
            onToggle={onToggleEditSection}
            mode={editorSectionMode}
            onSave={saveAllChanges}
            canSave={hasEditorChanges}
            saving={savingChallenge}
            icon={<CalendarIcon />}
            status="complete"
            title={t.createChallengePage.advancedSettingsTitle}
            subtitle={t.createChallengePage.advancedSettingsDescription}
          >
            <TournamentAdvancedSettingsFields
              challengeEventDraft={challengeEventDraft}
              periodChangeSaving={savingChallenge}
              setChallengeEventDraft={setChallengeEventDraft}
            />
          </EditorAccordionSection>
        )}
        <EditorAccordionSection
          id="participants"
          isOpen={activeEditSection === "participants"}
          onToggle={onToggleEditSection}
          mode={editorSectionMode}
          readOnly={finishedChallengeReadOnly}
          onSave={saveAllChanges}
          canSave={hasEditorChanges}
          saving={savingChallenge}
          icon={<TagIcon />}
          status={participantsStatus}
          title={
            isTournamentChallenge
              ? t.createChallengePage.tournamentCategoriesTitle
              : "Categorias e participantes"
          }
          subtitle={
            isTournamentChallenge
              ? t.createChallengePage.tournamentCategoriesEditDescription
              : "Gerencie categorias, inscritos, duplas e jogadores."
          }
        >
          {isTournamentChallenge &&
          tournamentCategoryModal &&
          updateTournamentCategoryModalDraft ? (
            <TournamentCategoryModal
              modal={tournamentCategoryModal}
              categoryOptions={categoryOptions}
              genderOptions={genderOptions}
              onChangeDraft={updateTournamentCategoryModalDraft}
              onClose={onCloseTournamentCategoryModal}
              onSave={onSaveTournamentCategory}
              saving={savingChallenge}
              submitLabel={siteTexts.common.save}
              categoryLabel={categoryLabel}
              categoryGenderLabel={categoryGenderLabel}
              token={token}
              embedded
            />
          ) : (
        <div className="field challenge-setup admin-form-block-participants">
          {participantNotice && (
            <p
              className="status-message admin-participants-status-message"
              role="status"
              aria-live="polite"
            >
              {participantNotice}
            </p>
          )}
          {hasRegistrationCategories && (
            <AdminTournamentCategoryTabs
              activeCategoryKey={activeTournamentCategoryKey}
              categories={challengeTournamentCategories}
              fixedPairCountsAsPairs={isPoco}
              onAddCategory={onAddTournamentCategory}
              onEditCategory={onEditTournamentCategory}
              onRemoveCategory={onRemoveTournamentCategory}
              onSelectCategory={onSelectTournamentCategory}
              participantsSummaryByCategory={
                tournamentParticipantsSummaryByCategory
              }
            />
          )}
          <div className="admin-painel">
            <div className="admin-bloco">
              <AdminChallengeParticipantControls
                availableUsers={availableUsersForChallenge}
                canAddParticipants={canAddParticipantsByFlow}
                currentPairMode={currentPairMode}
                draftPairsCount={draftPairs.length}
                draftPlayersCount={draftPlayers.length}
                drawCpf={drawCpf}
                isPoco={isPoco}
                isSuper8={isSuper8}
                isTournamentChallenge={hasRegistrationCategories}
                maxFixedPairsForCurrentChallenge={
                  maxFixedPairsForCurrentChallenge
                }
                maxDrawPlayersForCurrentChallenge={
                  maxDrawPlayersForCurrentChallenge
                }
                nameByCpf={nameByCpf}
                onAddDrawPlayer={onAddDrawPlayer}
                onAddFixedPair={onAddFixedPair}
                pairCpf1={pairCpf1}
                pairCpf2={pairCpf2}
                pairMode={pairMode}
                resolvePlayerCpfInput={resolvePlayerCpfInput}
                setDrawCpf={setDrawCpf}
                setPairCpf1={setPairCpf1}
                setPairCpf2={setPairCpf2}
                setPairMode={setPairMode}
                super8GroupACount={super8GroupAPlayers.length}
              />

              <div className="listas-duplas">
                {currentPairMode === "fixed" && !isSuper8 ? (
                  <AdminSelectedPairsList
                    adminPaymentMethod={adminPaymentMethod}
                    canAddRegistrations={
                      canAddRegistrationsForChallenge && canAddParticipantsByFlow
                    }
                    isAdmin={isAdmin}
                    isTournamentChallenge={hasRegistrationCategories}
                    loading={challengeEventRegistrationsLoading}
                    onAddTournamentPair={() => {
                      if (activeTournamentCategoryPairCapacity.exceeds) {
                        onError(
                          `Categoria atingiu o limite de ${activeTournamentCategoryPairCapacity.limit} ${
                            isPoco ? "duplas" : "inscritos"
                          }.`,
                        );
                        return;
                      }
                      onEditTournamentPair();
                    }}
                    onCopyPaymentLink={onCopyPaymentLink}
                    onDeleteRegistration={onDeleteRegistration}
                    onEditAvailabilityRestriction={setRestrictionModalRegistration}
                    onEditTournamentPair={onEditTournamentPair}
                    onMarkPaidRegistration={onMarkPaidRegistration}
                    onRemovePair={onRemovePair}
                    pairCapacity={activeTournamentCategoryPairCapacity}
                    pairCapacityUnit={isPoco ? "duplas" : "inscritos"}
                    pairs={draftPairs}
                    registrationByPairId={challengeRegistrationByPairId}
                  />
                ) : (
                  <>
                    {isSuper8 ? (
                      <AdminSuper8Groups
                        adminPaymentMethod={adminPaymentMethod}
                        groupAPlayers={super8GroupAPlayers}
                        isAdmin={isAdmin}
                        onCopyPaymentLink={onCopyPaymentLink}
                        onDeleteRegistration={onDeleteRegistration}
                        onEditAvailabilityRestriction={setRestrictionModalRegistration}
                        onMarkPaidRegistration={onMarkPaidRegistration}
                        onRejectRegistration={onRejectRegistration}
                        onRemovePlayer={onRemoveSuper8Player}
                        registrationByCpf={challengeRegistrationByCpf}
                        waitingPlayers={super8WaitingPlayers}
                      />
                    ) : (
                      <AdminSelectedPlayersList
                        adminPaymentMethod={adminPaymentMethod}
                        isAdmin={isAdmin}
                        loading={challengeEventRegistrationsLoading}
                        onCopyPaymentLink={onCopyPaymentLink}
                        onDeleteRegistration={onDeleteRegistration}
                        onEditAvailabilityRestriction={setRestrictionModalRegistration}
                        onMarkPaidRegistration={onMarkPaidRegistration}
                        onRefresh={() => {
                          onLoadChallengeEventRegistrations(
                            manualRegistrationEventIdForChallenge,
                          );
                        }}
                        onRejectRegistration={onRejectRegistration}
                        onRemovePlayer={onRemovePlayer}
                        onUpdatePlayerSide={onUpdatePlayerSide}
                        players={draftPlayers}
                        registrationByCpf={challengeRegistrationByCpf}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
        </EditorAccordionSection>

        <EditorAccordionSection
          id="shirts"
          isOpen={activeEditSection === "shirts"}
          onToggle={onToggleEditSection}
          mode={editorSectionMode}
          readOnly={finishedChallengeReadOnly}
          onSave={saveAllChanges}
          canSave={hasEditorChanges}
          saving={savingChallenge}
          footerActions={
            hasEnabledShirtConfiguration ? (
              <Button
                type="button"
                variant="pill"
                className="admin-download-shirts-button"
                onClick={onExportShirtSizes}
              >
                {t.createChallengePage.downloadShirtSizes}
              </Button>
            ) : null
          }
          icon={<CalendarIcon />}
          status="complete"
          title="Camisetas"
          subtitle="Configure somente tamanhos, prazo e disponibilidade."
        >
          <AdminChallengeShirtSettingsFields
            challengeEventDraft={challengeEventDraft}
            setChallengeEventDraft={setChallengeEventDraft}
          />
        </EditorAccordionSection>

        {!isTournamentChallenge && (
          <EditorAccordionSection
            id="rounds"
            isOpen={activeEditSection === "rounds"}
            onToggle={onToggleEditSection}
            mode={editorSectionMode}
            onSave={saveRoundsChanges}
            canSave={hasRoundsChanges}
            saving={savingChallenge}
            icon={<AwardIcon />}
            status={roundsStatus}
            title="Rodadas e resultados"
            subtitle="Revise confrontos, placares e vencedores."
          >
          {useCategoryRoundsAccordion ? (
            <AdminCategoryRoundsPanel
              categories={challengeTournamentCategories}
              activeCategoryKey={activeTournamentCategoryKey}
              onSelectCategory={onSelectTournamentCategory}
              onEditCategory={onEditTournamentCategory}
              onGenerate={() => void runGenerationAction(isSuper8 ? onGeneratePlayerBasedRounds : onGenerateFixedPairRounds)}
              canGenerate={canGenerateRoundsFromSummary}
              generating={savingChallenge || savingDrawPairs}
              hasRounds={Boolean(categoryRoundEntries.get(activeRoundsCategoryKey)?.length)}
              showGenerate={showGenerateRoundsAction}
              readOnly={finishedChallengeReadOnly}
              onSaveResults={() => void saveRoundsChanges()}
              canSaveResults={hasRoundsChanges}
            >
              {challengeTournamentCategories.filter(category => category.categoryKey === activeRoundsCategoryKey).map((category) => {
                const categoryKey = String(category.categoryKey || "").trim();
                const roundEntries = categoryRoundEntries.get(categoryKey) || [];
                const categoryRounds = roundEntries.map((entry) => entry.round);
                return (
                  <div key={`challenge-category-rounds-${categoryKey}`}>
                        {isPoco && roundEntries.length > 0 && !finishedChallengeReadOnly &&
                          <PocoGeneratedCourtsEditor rounds={rounds} categoryKey={categoryKey} pairs={draftPairs}
                            registeredPairs={challengeDetail.pairs}
                            disabled={savingChallenge || savingDrawPairs}
                            onChange={next => setChallengeRoundsJson(JSON.stringify(next, null, 2))} />}
                        {!isSuper8 && currentPairMode !== "fixed" && (
                          <div className="admin-rounds-generation-actions">
                            <AdminDrawPairsSummary
                              canGenerateRounds={false}
                              nameByCpf={nameByCpf}
                              onGenerateRounds={onGenerateFixedPairRounds}
                              pairs={draftPairs}
                              players={draftPlayers}
                              resolveSideLabel={resolveSideLabel}
                              saving={savingDrawPairs}
                              showGenerateRounds={false}
                            />
                          </div>
                        )}
                        <AdminChallengeRoundsEditor
                          challengeDetail={challengeDetail}
                          emptyMessage={
                            currentPairMode === "draw"
                              ? siteTexts.challengeEngine.labels.startDrawMessage
                              : siteTexts.challengeEngine.labels.startMessage
                          }
                          finalLabel={t.historyList.final}
                          isPoco={isPoco}
                          isSuper8={isSuper8}
                          isTournamentChallenge={false}
                          onClearWinner={(roundIndex, matchIndex) =>
                            showClearRoundWinnerAction
                              ? onClearRoundWinner(
                                  roundEntries[roundIndex]?.sourceIndex ?? roundIndex,
                                  matchIndex,
                                )
                              : undefined
                          }
                          onToggleCheckInSide={(side, checkedIn) =>
                            void toggleCheckInSide(side, checkedIn)
                          }
                          onToggleCourtStatus={(roundIndex, matchIndex, match, status) =>
                            void toggleCourtStatus(
                              roundEntries[roundIndex]?.sourceIndex ?? roundIndex,
                              matchIndex,
                              match,
                              status,
                            )
                          }
                          onUpdatePocoWinner={(roundIndex, matchIndex, winnerSide) =>
                            onUpdatePocoWinner(
                              roundEntries[roundIndex]?.sourceIndex ?? roundIndex,
                              matchIndex,
                              winnerSide,
                            )
                          }
                          onUpdateRoundScore={(roundIndex, matchIndex, field, value) =>
                            onUpdateRoundScore(
                              roundEntries[roundIndex]?.sourceIndex ?? roundIndex,
                              matchIndex,
                              field,
                              value,
                            )
                          }
                          pairLabelById={pairLabelById}
                          participantCheckins={
                            challengeDetail.participantCheckins || []
                          }
                          pendingCheckInSideId={pendingCheckInSideId}
                          pendingCourtMatchId={pendingCourtMatchId}
                          participantsCountForGroup={Number(
                            challengeSelectedParticipantsCount || 0,
                          )}
                          resolveRoundPairLabelWithPoints={(pairId, localRoundIndex) =>
                            resolveRoundPairLabelWithPoints(pairId, roundEntries[localRoundIndex]?.sourceIndex ?? localRoundIndex)
                          }
                          resultsOnly={finishedChallengeReadOnly}
                          rounds={categoryRounds}
                          scoreMax={scoreMax}
                          showClearAction={showClearRoundWinnerAction}
                          courtsCount={category.courtsCount ?? challengeDetail.courtsCount}
                        />
                  </div>
                );
              })}
            </AdminCategoryRoundsPanel>
          ) : (
            <>
              {!isSuper8 && currentPairMode !== "fixed" && (
                <div className="admin-rounds-generation-actions">
                  <AdminDrawPairsSummary
                    canGenerateRounds={false}
                    nameByCpf={nameByCpf}
                    onGenerateRounds={onGenerateFixedPairRounds}
                    pairs={draftPairs}
                    players={draftPlayers}
                    resolveSideLabel={resolveSideLabel}
                    saving={savingDrawPairs}
                    showGenerateRounds={false}
                  />
                </div>
              )}
              <AdminChallengeRoundsEditor
                challengeDetail={challengeDetail}
                emptyMessage={
                  currentPairMode === "draw"
                    ? siteTexts.challengeEngine.labels.startDrawMessage
                    : siteTexts.challengeEngine.labels.startMessage
                }
                finalLabel={t.historyList.final}
                isPoco={isPoco}
                isSuper8={isSuper8}
                isTournamentChallenge={false}
                onClearWinner={(roundIndex, matchIndex) =>
                  showClearRoundWinnerAction
                    ? onClearRoundWinner(roundIndex, matchIndex)
                    : undefined
                }
                onToggleCheckInSide={(side, checkedIn) =>
                  void toggleCheckInSide(side, checkedIn)
                }
                onToggleCourtStatus={(roundIndex, matchIndex, match, status) =>
                  void toggleCourtStatus(roundIndex, matchIndex, match, status)
                }
                onUpdatePocoWinner={onUpdatePocoWinner}
                onUpdateRoundScore={onUpdateRoundScore}
                pairLabelById={pairLabelById}
                participantCheckins={challengeDetail.participantCheckins || []}
                pendingCheckInSideId={pendingCheckInSideId}
                pendingCourtMatchId={pendingCourtMatchId}
                participantsCountForGroup={Number(
                  challengeSelectedParticipantsCount || 0,
                )}
                resolveRoundPairLabelWithPoints={resolveRoundPairLabelWithPoints}
                resultsOnly={finishedChallengeReadOnly}
                rounds={rounds}
                scoreMax={scoreMax}
                showClearAction={showClearRoundWinnerAction}
                courtsCount={challengeDetail.courtsCount}
              />
            </>
          )}
          </EditorAccordionSection>
        )}
        {isTournamentOperations && (
          <EditorAccordionSection
            id="tournament-event-schedule"
            isOpen={activeEditSection === "tournament-event-schedule"}
            onToggle={onToggleEditSection}
            mode={editorSectionMode}
            onSave={saveAllChanges}
            canSave={hasEditorChanges}
            saving={savingChallenge}
            icon={<CalendarIcon />}
            status={tournamentScheduleStatus}
            title={t.createChallengePage.tournamentEventScheduleTitle}
            subtitle={t.createChallengePage.tournamentEventScheduleDescription}
          >
            <AdminTournamentEventSchedulePanel
              ref={tournamentEventScheduleRef}
              token={token}
              tournamentId={Number(challengeDetail.id)}
              onPendingChangesChange={setTournamentEventSchedulePendingChangeItems}
              onError={onError}
              onSuccess={onSuccess}
              scheduleEndAt={challengeScheduledEndAtDraft?.toISOString() || null}
              scheduleStartAt={challengeScheduledAtDraft?.toISOString() || null}
            />
          </EditorAccordionSection>
        )}
        {isTournamentOperations && (
          <EditorAccordionSection
            id="tournament-operations"
            isOpen={activeEditSection === "tournament-operations"}
            keepMountedWhenClosed
            onToggle={onToggleEditSection}
            mode={editorSectionMode}
            onSave={saveAllChanges}
            canSave={hasEditorChanges}
            saving={savingChallenge}
            icon={<AwardIcon />}
            status={tournamentOperationsStatus}
            title={t.createChallengePage.tournamentOperationsTitle}
            subtitle={t.createChallengePage.tournamentOperationsDescription}
          >
            <AdminTournamentOperationsPanel
              ref={tournamentOperationsRef}
              token={token}
              tournamentId={Number(challengeDetail.id)}
              categories={challengeTournamentCategories}
              activeCategoryKey={activeTournamentCategoryKey}
              onError={onError}
              onPendingChangesChange={setTournamentPendingChangeItems}
              onSuccess={onSuccess}
              scheduleEndAt={challengeScheduledEndAtDraft?.toISOString() || null}
              scheduleCourtSettingsHostId={tournamentCourtSettingsHostId}
              scheduleSettingsHostId={tournamentScheduleSettingsHostId}
              scheduleStartAt={challengeScheduledAtDraft?.toISOString() || null}
              tournamentStarted={
                Boolean(challengeDetail.startedAt) ||
                challengeDetail.status === "started" ||
                challengeDetail.status === "finished"
              }
              tournamentRegistrationsOpen={registrationsAreOpen}
              tournamentFinished={challengeDetail.status === "finished"}
            />
          </EditorAccordionSection>
        )}
        </div>
        <aside className="admin-event-live-summary" aria-label={liveSummaryLabel}>
          <span className="admin-event-live-summary-kicker">{liveSummaryLabel}</span>
          <strong>{challengeDetail.challengeName || editorTitle}</strong>
          <div className="admin-event-live-summary-list">
            <span>
              <AwardIcon aria-hidden="true" />
              {isTournamentChallenge
                ? t.createChallengePage.summary.tournament
                : challengeDetail.challengeType ||
                  t.createChallengePage.summary.challenge}
            </span>
            <span>
              <CalendarIcon aria-hidden="true" />
              {editSummaryDateTime}
            </span>
            <span>
              <MapPinIcon aria-hidden="true" />
              {summaryLocation || t.createChallengePage.summary.locationFallback}
            </span>
            <span>
              <TagIcon aria-hidden="true" />
              {summaryCategories}
            </span>
            <span>
              <EditIcon aria-hidden="true" />
              {challengeSelectedParticipantsCount}{" "}
              {t.createChallengePage.summary.participantsSuffix}
            </span>
          </div>
          <div className="admin-event-live-summary-actions">
            {!isTournamentChallenge && (
              <div className="admin-event-operation-box">
                <div
                  className={`admin-event-operation-status ${
                    registrationsAreOpen ? "is-open" : "is-closed"
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {registrationsAreOpen ? (
                    <UnlockIcon aria-hidden="true" />
                  ) : (
                    <LockIcon aria-hidden="true" />
                  )}
                  <span>
                    <small>{operationTexts.registrations}</small>
                    <strong>
                      {registrationsAreOpen
                        ? operationTexts.registrationsOpen
                        : operationTexts.registrationsClosed}
                    </strong>
                  </span>
                </div>

                {isPendingChallenge && (
                  <div className="admin-event-operation-buttons">
                    {(registrationsAreOpen || !hasGeneratedRounds) && (
                      <Button
                        type="button"
                        variant="pill"
                        disabled={registrationsToggleLoading}
                        onClick={() => {
                          if (registrationsAreOpen) {
                            setConfirmCloseRegistrations(true);
                            return;
                          }
                          void runToggleRegistrations();
                        }}
                      >
                        {registrationsAreOpen ? (
                          <LockIcon aria-hidden="true" />
                        ) : (
                          <UnlockIcon aria-hidden="true" />
                        )}
                        {registrationsToggleLoading
                          ? t.historyList.saving
                          : registrationsAreOpen
                            ? operationTexts.closeRegistrations
                            : operationTexts.openRegistrations}
                      </Button>
                    )}

                    {showPairDrawAction && (
                      <Button
                        type="button"
                        variant="pill"
                        disabled={
                          !canGeneratePlayerBasedRounds ||
                          !canRunPairDrawByFlow ||
                          savingDrawPairs ||
                          savingChallenge
                        }
                        onClick={() =>
                          void runGenerationAction(
                            onGeneratePlayerBasedRounds,
                          )
                        }
                      >
                        <RefreshIcon aria-hidden="true" />
                        {draftPairs.length > 0
                          ? operationTexts.redrawPairs
                          : siteTexts.challengeEngine.labels.drawPairs}
                      </Button>
                    )}

                    {showGenerateRoundsAction && !useCategoryRoundsAccordion && (
                      <Button
                        type="button"
                        variant="pill"
                        disabled={
                          !canGenerateRoundsFromSummary || savingChallenge
                        }
                        onClick={() =>
                          void runGenerationAction(
                            isSuper8
                              ? onGeneratePlayerBasedRounds
                              : onGenerateFixedPairRounds,
                          )
                        }
                      >
                        <RefreshIcon aria-hidden="true" />
                        {hasGeneratedRounds
                          ? operationTexts.regenerateRounds
                          : siteTexts.challengeEngine.labels.generateRounds}
                      </Button>
                    )}

                    <Button
                      type="button"
                      variant="primary"
                      disabled={!canStartFromSummary || startingChallenge}
                      onClick={() => void runStartChallenge()}
                      title={
                        registrationsAreOpen
                          ? operationTexts.closeBeforeStart
                          : !hasGeneratedRounds
                            ? operationTexts.generateBeforeStart
                            : undefined
                      }
                    >
                      <PlayIcon aria-hidden="true" />
                      {startingChallenge
                        ? t.historyList.saving
                        : t.actions.startChallenge}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {(editorChangeItems.length > 0 || requiredMissingItems.length > 0) && (
              <div className="admin-editor-pending-changes">
                {editorChangeItems.length > 0 && (
                  <div className="admin-editor-pending-changes-group">
                    <span>{t.createChallengePage.summary.changes}</span>
                    <ul>
                      {editorChangeItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {requiredMissingItems.length > 0 && (
                  <div className="admin-editor-pending-changes-group is-required">
                    <span>{t.createChallengePage.summary.pending}</span>
                    <ul>
                      {requiredMissingItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {!isTournamentChallenge && hasEnabledShirtConfiguration && (
              <Button
                type="button"
                variant="pill"
                className="admin-download-shirts-button"
                onClick={onExportShirtSizes}
              >
                {t.createChallengePage.downloadShirtSizes}
              </Button>
            )}
            {canFinishChallenge && challengeDetail.status === "started" && (
              <Button
                type="button"
                variant="pill"
                onClick={() => void onFinishChallenge()}
              >
                <AwardIcon aria-hidden="true" />
                {t.actions.finishChallenge}
              </Button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
